import React from "react";

/**
 * StorageMeter — minimal quota bar.
 *
 * Props:
 *  - data: { used_bytes, limit_bytes, percent, override_gb?, package_limit_gb? }
 *  - testid: optional data-testid prefix
 *  - compact: boolean — narrower variant for sidebars
 */
export default function StorageMeter({ data, testid = "storage-meter", compact = false }) {
  if (!data) return null;
  const used = data.used_bytes || 0;
  const limit = data.limit_bytes || 0;
  const pct = Math.min(100, data.percent || 0);
  const tone = pct >= 95 ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
  const warn = pct >= 95 ? "Quota exceeded — uploads will be blocked." :
               pct >= 80 ? "Approaching storage limit." : null;
  return (
    <div data-testid={testid} className={compact ? "" : "rounded-xl border border-zinc-200 bg-white p-4"}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Storage</div>
        <div className="text-xs text-zinc-600" data-testid={`${testid}-text`}>
          <span className="font-medium text-zinc-900">{fmtGB(used)}</span>
          <span className="text-zinc-400"> / {fmtGB(limit)} GB</span>
        </div>
      </div>
      <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
        <div className={`h-full ${tone} transition-all duration-500`} style={{ width: `${pct}%` }} data-testid={`${testid}-bar`} />
      </div>
      <div className="flex items-center justify-between mt-2">
        <div className="text-[11px] text-zinc-500">{pct.toFixed(1)}% used</div>
        {data.override_gb != null ? (
          <div className="text-[11px] text-zinc-500">Admin override · {data.override_gb} GB</div>
        ) : data.package_limit_gb != null ? (
          <div className="text-[11px] text-zinc-500">Package · {data.package_limit_gb} GB</div>
        ) : (
          <div className="text-[11px] text-zinc-400">Default tier</div>
        )}
      </div>
      {warn && (
        <div className={`mt-3 px-3 py-2 rounded-md text-xs ${pct >= 95 ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`} data-testid={`${testid}-warning`}>
          {warn}
        </div>
      )}
    </div>
  );
}

function fmtGB(b) {
  if (!b) return "0";
  const gb = b / (1024 ** 3);
  if (gb >= 1) return gb.toFixed(2);
  const mb = b / (1024 ** 2);
  return `${mb.toFixed(1)}MB`.replace("MB", " MB");
}
